import { Badge, Box, HStack, Input, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuSearch, LuTarget } from "react-icons/lu";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getLinkedinCampaigns } from "../api/integration";
import { ActivityLog } from "../components/activity-log";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { ChangeCell, ChangeDot } from "../components/change-cell";
import DataTable from "../components/data-table";
import { DeltaBadge } from "../components/delta-badge";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import { InputGroup } from "../components/input-group";
import { LinkedinAgentSwitch } from "../components/linkedin-agent-switch";
import { LinkedinCampaignStatusPill } from "../components/linkedin-campaign-status-pill";
import Page from "../components/page";
import PageHeader from "../components/page-header";
import { UtcTimestamp } from "../components/utc-timestamp";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useWorkspaceURL,
} from "../hooks";
import { useLinkedinChanges } from "../hooks/linkedin-changes";
import { brand } from "../lib/brand";
import {
  formatCurrency,
  formatDistanceShort,
  formatStatus,
} from "../lib/format";
import { changeLabel } from "../lib/linkedin-change";
import {
  agentDrivenDays,
  calculateMetrics,
  getCostPerOutcome,
  getMetricType,
  isSupportedLinkedinCampaign,
} from "../lib/linkedin-metrics";
import {
  loadLinkedinAdAccounts,
  selectLinkedinAdAccountById,
} from "../store/linkedinSlice";
import {
  LinkedinAgentStatus,
  LinkedinCampaign,
  LinkedinChange,
} from "../types/linkedin";

// Every track but the first stays a fixed width because the header and
// every row are separate grids -- an auto track sizes itself per row,
// which staggers the column borders down the table.
const CAMPAIGN_TABLE_TEMPLATE =
  "minmax(260px,1fr) 116px 150px 176px 144px 250px";

export default function LinkedInCampaignsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { workspace, adAccountId } = useParams();
  const workspaceURL = useWorkspaceURL();
  const [searchParams, setSearchParams] = useSearchParams();

  const [campaigns, setCampaigns] = React.useState<LinkedinCampaign[]>([]);
  const [isLoadingCampaigns, wrapIsLoadingCampaigns] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  // Bumped after the agent is switched on or off so the log refetches.
  const [changesVersion, setChangesVersion] = React.useState(0);

  const adAccount = useAppSelector((state) =>
    selectLinkedinAdAccountById(parseInt(adAccountId || "0"))(state),
  );
  const adAccountsLoaded = useAppSelector(
    (state) => state.linkedin.adAccounts.length > 0,
  );

  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "LinkedIn", to: workspaceURL("linkedin-ad-accounts") },
    { label: adAccount?.name ?? "…" },
  ]);

  const searchQuery = searchParams.get("search") || "";

  const loadCampaigns = React.useCallback(async () => {
    if (!adAccountId) return;
    await wrapIsLoadingCampaigns(async () => {
      const campaignList = await getLinkedinCampaigns(
        workspace,
        parseInt(adAccountId),
      );
      setCampaigns(campaignList);
      setHasLoadedOnce(true);
    });
  }, [adAccountId, workspace, wrapIsLoadingCampaigns]);

  React.useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  React.useEffect(() => {
    if (!adAccountsLoaded) {
      dispatch(loadLinkedinAdAccounts());
    }
  }, [adAccountsLoaded, dispatch]);

  const handleAgentChange = React.useCallback(async () => {
    await loadCampaigns();
    setChangesVersion((version) => version + 1);
  }, [loadCampaigns]);

  const setSearch = (value: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) {
        params.set("search", value);
      } else {
        params.delete("search");
      }
      return params;
    });
  };

  const campaignURL = (campaignId: number) =>
    workspaceURL(
      `linkedin-ad-accounts/${adAccountId}/campaigns/${campaignId}/agent`,
    );

  const filteredCampaigns = React.useMemo(
    () =>
      campaigns.filter(
        (campaign) =>
          !searchQuery ||
          campaign.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [campaigns, searchQuery],
  );

  return (
    <Page loading={isLoadingCampaigns && !hasLoadedOnce}>
      <Stack flex="1" gap="6">
        <PageHeader
          title="Campaigns"
          subtitle={`When enabled, ${brand.productName} improves the campaign's bids and budget.`}
        />

        <InputGroup
          startElement={<LuSearch size="16" />}
          width={"324px" as any}
          maxWidth="full"
        >
          <Input
            placeholder="Search for a campaign"
            value={searchQuery}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>

        {filteredCampaigns.length === 0 && !isLoadingCampaigns ? (
          <HStack flex="1" alignItems="center" paddingY="8">
            <EmptyState
              icon={<IconCircle Icon={LuTarget} size="md" variant="flat" />}
              title={
                campaigns.length === 0
                  ? "No Campaigns"
                  : "No Matching Campaigns"
              }
              description={
                campaigns.length === 0
                  ? "No campaigns found for this ad account."
                  : "No campaigns match your search."
              }
            />
          </HStack>
        ) : (
          <DataTable
            data={filteredCampaigns}
            skeleton={isLoadingCampaigns && hasLoadedOnce ? 3 : undefined}
            template={CAMPAIGN_TABLE_TEMPLATE}
            minTableWidth="1100px"
            pagination
            rowHref={(campaign) =>
              isSupportedLinkedinCampaign(campaign)
                ? campaignURL(campaign.id)
                : null
            }
            columns={[
              {
                name: "Campaign",
                cell: ({ value: campaign }) => (
                  <HStack gap="1.5" minWidth="0">
                    <Text fontWeight="medium" truncate title={campaign.name}>
                      {campaign.name}
                    </Text>
                    {campaign["test?"] && (
                      <Badge
                        colorPalette="orange"
                        size="xs"
                        textTransform="uppercase"
                        variant="subtle"
                      >
                        Test
                      </Badge>
                    )}
                    {!isSupportedLinkedinCampaign(campaign) && (
                      <Badge
                        colorPalette="gray"
                        size="xs"
                        textTransform="uppercase"
                        variant="subtle"
                      >
                        No daily budget
                      </Badge>
                    )}
                  </HStack>
                ),
              },
              {
                name: "Status",
                cell: ({ value: campaign }) => (
                  <LinkedinCampaignStatusPill status={campaign.status} />
                ),
              },
              {
                name: "Type",
                cell: ({ value: campaign }) => (
                  <Text truncate>{formatStatus(campaign.objective)}</Text>
                ),
              },
              {
                name: "Cost per result",
                cell: ({ value: campaign }) => <CprCell campaign={campaign} />,
              },
              {
                name: brand.productName,
                cell: ({ value: campaign }) => {
                  const on =
                    campaign.agent?.status === LinkedinAgentStatus.ACTIVE;
                  return (
                    // Raised above the row's link overlay so flipping the
                    // switch doesn't open the campaign.
                    <Box position="relative" zIndex={"1" as any}>
                      <LinkedinAgentSwitch
                        adAccountId={parseInt(adAccountId!)}
                        campaignId={campaign.id}
                        campaignName={campaign.name}
                        disabled={!isSupportedLinkedinCampaign(campaign)}
                        on={on}
                        onToggled={handleAgentChange}
                        workspace={workspace!}
                      >
                        {on ? "On" : "Off"}
                      </LinkedinAgentSwitch>
                    </Box>
                  );
                },
              },
              {
                name: "Recent changes",
                cell: ({ value: campaign }) => (
                  <RecentChangeCell change={campaign["last-change"] ?? null} />
                ),
              },
            ]}
          />
        )}

        {adAccountId && (
          <AccountActivityLog
            adAccountId={parseInt(adAccountId)}
            campaigns={campaigns}
            onOpenCampaign={(campaignId) => navigate(campaignURL(campaignId))}
            version={changesVersion}
            workspace={workspace!}
          />
        )}
      </Stack>
    </Page>
  );
}

function CprCell({ campaign }: { campaign: LinkedinCampaign }) {
  if (!campaign.stats) return <Text>—</Text>;
  const driven = agentDrivenDays(campaign.stats.post, campaign.agent);
  if (driven.length === 0) return <Text>—</Text>;
  const metricType = getMetricType(campaign.objective, campaign["cost-type"]);
  const post = getCostPerOutcome(calculateMetrics(driven), metricType);
  const pre =
    campaign.stats.pre.length > 0
      ? getCostPerOutcome(calculateMetrics(campaign.stats.pre), metricType)
      : null;
  // The chip beside it won't shrink, so the figure is what gives when a
  // workspace's costs run longer than the track.
  const formatted = formatCurrency(post);
  return (
    <>
      <Text
        title={
          pre !== null
            ? `vs ${formatCurrency(pre)} without ${brand.productName}`
            : formatted
        }
        truncate
      >
        {formatted}
      </Text>
      {pre !== null && <DeltaBadge now={post} before={pre} lowerIsBetter />}
    </>
  );
}

function RecentChangeCell({ change }: { change: LinkedinChange | null }) {
  if (!change) return <Text color="fg.muted">No recent changes</Text>;
  return (
    <HStack gap="2" minWidth="0">
      <ChangeDot kind={change.kind} />
      <Text fontWeight="medium" truncate>
        {changeLabel(change)}
      </Text>
      <Text color="fg.muted" whiteSpace="nowrap">
        {formatDistanceShort(change["created-at"])}
      </Text>
    </HStack>
  );
}

function AccountActivityLog({
  adAccountId,
  campaigns,
  onOpenCampaign,
  version,
  workspace,
}: {
  adAccountId: number;
  campaigns: LinkedinCampaign[];
  onOpenCampaign: (campaignId: number) => void;
  version: number;
  workspace: string;
}) {
  const feed = useLinkedinChanges(workspace, adAccountId, { version });

  const campaignNames = React.useMemo(() => {
    const names = new Map<number, string>();
    for (const campaign of campaigns) {
      names.set(campaign.id, campaign.name);
    }
    return names;
  }, [campaigns]);

  return (
    <ActivityLog defaultOpen meta={`${feed.total} changes in the last 30 days`}>
      {feed.total === 0 && !feed.isLoading ? (
        <Box background="white" boxShadow="insetRing" padding="6">
          <Text color="fg.muted" fontSize="sm">
            No changes yet.
          </Text>
        </Box>
      ) : (
        <DataTable
          bare
          data={feed.rows}
          skeleton={feed.isLoading && feed.rows.length === 0 ? 3 : undefined}
          template="170px minmax(220px,1fr) minmax(300px,1.1fr)"
          minTableWidth="760px"
          onRowClick={(row) => onOpenCampaign(row["campaign-id"])}
          pagination={feed.pagination}
          columns={[
            {
              name: "Date (UTC)",
              cell: ({ value }) => (
                <UtcTimestamp color="fg.muted" date={value["created-at"]} />
              ),
            },
            {
              name: "Campaign",
              cell: ({ value }) => (
                <Text fontWeight="medium" truncate>
                  {campaignNames.get(value["campaign-id"]) ??
                    `Campaign ${value["campaign-id"]}`}
                </Text>
              ),
            },
            {
              name: "Change",
              cell: ({ value }) => <ChangeCell change={value} />,
            },
          ]}
        />
      )}
    </ActivityLog>
  );
}
